<template>
    <div class="web-page-automation-modal">
        <GoalModal
            data-qa="web-page-automation-modal-goal"
            :is-open="isOpen"
            :title="$t('funnel.edit.goal.websiteTrigger.instruction.title')"
            :summary-text="$t('funnel.edit.goal.websiteTrigger.instruction.introduction')"
            :goal-tip-text="$t('funnel.edit.goal.websiteTrigger.instruction.tip.overview')"
            :goal-tip-link="toolTipLink"
            show-save
            @update-config="updateConfig"
            @close-modal="closeModal"
        >
            <template #goalParameters>
                <div class="vertical-container">
                    <span class="instruction1">{{ $t('funnel.edit.goal.websiteTrigger.instruction.instruction1') }}</span>

                    <div class="copy-code-snippet">
                        <DsTextAreaField
                            v-model.trim="trackingCode"
                            data-qa="tracking-code-value-qa"
                            readonly
                            class="text-area-input-field"
                        />
                        <DsOutlineButton
                            @click="copy(trackingCode)"
                        >
                            <template #leading>
                                <DsIcon class="copy-icon" name="copy" />
                            </template>
                            <template #default>
                                {{ $t('funnel.edit.goal.websiteTrigger.instruction.snippet.button') }}
                            </template>
                        </DsOutlineButton>
                    </div>

                    <span class="instruction2">{{ $t('funnel.edit.goal.websiteTrigger.instruction.instruction2') }}</span>

                    <div class="urls-container">
                        <div
                            v-for="(item) in websiteTriggerUrls"
                            :key="item.id"
                            class="new-url-container"
                        >
                            <div class="wrap-input">
                                <DsInputField
                                    v-model="item.url"
                                    data-qa="item-url-data-qa"
                                    :label="$t('funnel.edit.goal.websiteTrigger.table.urlHeader')"
                                    required
                                    class="new-url-input-field"
                                    @blur="onBlurItemUrl(item)"
                                />
                                <div v-if="item.invalidUrl" class="error-message">
                                    {{ $t('funnel.edit.goal.websiteTrigger.field.required') }}
                                </div>
                            </div>
                            <span class="circle-button-span" @click="removeBox(item.id)">
                                <DsIcon name="trash-2" class="circle-button-remove" />
                            </span>
                        </div>
                        <div v-show="showNewInput" class="new-url-container">
                            <div class="wrap-input">
                                <DsInputField
                                    v-model="newUrl"
                                    :label="$t('funnel.edit.goal.websiteTrigger.table.urlHeader')"
                                    required
                                    class="new-url-input-field"
                                    @blur="onBlurSaveNewUrl"
                                />
                                <div v-if="newUrlInvalid" class="error-message">
                                    {{ $t('funnel.edit.goal.websiteTrigger.field.required') }}
                                </div>
                            </div>
                            <span class="circle-button-span" @click="removeEmpty">
                                <DsIcon name="trash-2" class="circle-button-remove" />
                            </span>
                        </div>
                    </div>

                    <span
                        class="add-field-span"
                        @click="showAddBox"
                    >
                        <DsIcon name="add" class="plus-button-add" />
                        <span class="add-url-text-button">{{ $t('funnel.edit.goal.websiteTrigger.url.addUrlButton') }}</span>
                    </span>
                </div>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import { triggerCodeAndUrls, saveConfig } from './web.page.automation.api';

export default {
    components: { GoalModal },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    urlIds: [],
                },
            }),
        },
        funnelId: {
            type: Number,
            required: true,
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            trackingCode: '',
            websiteTriggerUrls: [],
            newUrl: null,
            newUrlInvalid: false,
        };
    },

    computed: {
        showNewInput() {
            return this.newUrl !== null;
        },

        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },

        toolTipLink() {
            return this.isSpaApp ? this.$t('funnel.edit.goal.websiteTrigger.instruction.tip.url.keap') : this.$t('funnel.edit.goal.websiteTrigger.instruction.tip.url.classic');
        },
    },

    mounted() {
        this.loadCodeAndUrls();
    },

    methods: {
        async loadCodeAndUrls() {
            const { trackingCode, urls, success } = await triggerCodeAndUrls(this.funnelId, this.cell.id);

            if (success) {
                const parser = new DOMParser();
                const decodedString = parser.parseFromString(`<!doctype html><body>${trackingCode}`, 'text/html').body.textContent;

                this.trackingCode = decodedString;
                this.websiteTriggerUrls = Array.from(urls).map((item) => {
                    return {
                        id: item.id,
                        url: item.url,
                        deleted: item.deleted,
                        invalidUrl: false,
                    };
                });
            }
        },

        async updateConfig() {
            const body = {
                funnelId: this.funnelId,
                goalId: this.cell.id,
                websiteTriggerUrls: this.websiteTriggerUrls
                    .filter((item) => this.isValidUrl(item.url))
                    .map((item) => ({
                        id: item.id,
                        url: item.url,
                    })),
            };

            try {
                const cell = { ...this.cell };

                cell.value = cell.value || {};

                cell.value.urlIds = [];
                const savedUrlIds = await saveConfig(body);

                savedUrlIds.forEach((urlId) => {
                    const urlIdLong = `${urlId}L`;

                    if (urlId > 0 && !cell.value.urlIds.includes(urlIdLong)) {
                        cell.value.urlIds.push(urlIdLong);
                    }
                });

                this.$emit('update-cell-value', { cell });
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.goal.websiteTrigger.validation.configuration');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        closeModal() {
            this.$emit('close-modal');
        },
        copy(scriptSnippet) {
            const successHeadLine = this.$t('funnel.edit.goal.websiteTrigger.copySuccess');
            const successMessage = this.$t('funnel.edit.goal.websiteTrigger.copyAltText');
            const errorMessage = this.$t('funnel.edit.goal.websiteTrigger.validation.copy');

            return this.$copyText(scriptSnippet).then(() => {
                this.$toast({
                    headline: successHeadLine,
                    message: successMessage,
                });
            }, () => {
                this.$error({
                    message: errorMessage,
                });
            });
        },
        showAlreadyDuplicatedError() {
            const errorDuplicateMessage = this.$t('funnel.edit.goal.websiteTrigger.validation.duplicateUrl');

            this.$error({
                message: errorDuplicateMessage,
            });
        },
        onBlurItemUrl(item) {
            const dupe = this.websiteTriggerUrls.some((i) => i.url === item.url && i.id !== item.id);

            if (dupe) {
                this.showAlreadyDuplicatedError();
            } else {
                const itemUrl = item.url;

                item.invalidUrl = !this.isValidUrl(itemUrl);
            }
        },
        onBlurSaveNewUrl() {
            if (this.newUrl !== '') {
                if (this.validateDuplicated(this.newUrl)) {
                    this.showAlreadyDuplicatedError();
                } else if (this.isValidUrl(this.newUrl)) {
                    const newUrlObject = {
                        id: this.websiteTriggerUrls.length + 1,
                        url: this.newUrl,
                        invalidUrl: false,
                    };

                    this.websiteTriggerUrls = [...this.websiteTriggerUrls, newUrlObject];
                    this.newUrl = null;
                    this.newUrlInvalid = false;
                } else {
                    this.newUrlInvalid = true;
                }
            }
        },
        isValidUrl(url) {
            const urlRegex = /^(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9]+(?:\.[a-zA-Z]{2,})+(?:\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?(?:#[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;

            return urlRegex.test(url);
        },
        validateDuplicated(url) {
            return this.websiteTriggerUrls.some((item) => item.url === url);
        },
        showAddBox() {
            if (this.newUrl === null) {
                this.newUrl = '';
            }
        },
        removeBox(id) {
            if (id) {
                const urls = [...this.websiteTriggerUrls];

                this.websiteTriggerUrls = urls.filter((url) => url.id !== id);
            } else {
                this.newUrl = null;
            }
        },
        removeEmpty() {
            this.newUrl = null;
            this.newUrlInvalid = false;
        },
    },
};
</script>
<style lang="scss">
.copy-code-snippet .input-field textarea {
    min-height: px-to-rem(80);
}
</style>
<style lang="scss" scoped>
.vertical-container {
    display: flex;
    flex-direction: column;
}
.new-url-container {
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: $spacing-250;
}

.new-url-input-field {
    flex: 1;
    height: px-to-rem(40);
    margin: 0px $spacing-125 $spacing-075 0px;
}

.circle-button-span {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 8%;
    height: px-to-rem(40);
    cursor: pointer;
    margin-bottom: $spacing-100;
    margin-left: $spacing-100;
}

.circle-button-remove {
    margin-right: $spacing-075;
    color: $color-ink-900;
}

.add-field-span {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    cursor: pointer;
    height: $spacing-400;
    margin-bottom: $spacing-250;
}

.plus-button-add {
    color: $color-blue-900;
}

.add-url-text-button {
    color: $color-blue-900;
}

.copy-code-snippet {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding-bottom: $spacing-125;
}

.vertical-container > .instruction1 {
    margin-bottom: $spacing-250;
}

.vertical-container > .instruction2 {
    margin-top: $spacing-125;
}

.vertical-container > .urls-container {
    margin-top: $spacing-250;
}

.text-area-input-field {
    word-break: break-all;
    white-space: pre-wrap;
}

.wrap-input {
    display: flex;
    flex-direction: column;
    width: 92%;
}

.error-message {
    color: red;
    font-size: $spacing-150;
}

</style>
